import { levelLabelOf } from '@innermatelic/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type CustomerRow, type LicenseRow } from '../../lib/api.js';
import { CustomerUsersTab } from './Users.js';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'users'>('overview');

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.get<{ customers: CustomerRow[] }>('/api/admin/customers'),
      api.get<{ licenses: LicenseRow[] }>('/api/admin/licenses'),
    ])
      .then(([c, l]) => {
        const row = c.customers.find((x) => x.id === id) ?? null;
        setCustomer(row);
        setLicenses(l.licenses.filter((x) => x.customerId === id));
      })
      .catch((err) => setError((err as Error).message));
  }, [id]);

  if (!id) return null;
  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!customer) {
    return <div className="text-neutral-500">로딩…</div>;
  }

  return (
    <div>
      <Link to="/admin/customers" className="text-xs text-neutral-500 hover:text-neutral-800">
        ← 고객사 목록
      </Link>
      <header className="mt-2 mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{customer.name}</h1>
        <p className="mt-1 text-sm text-neutral-500">
          {customer.contactEmail ?? '담당자 미지정'} · {customer.domain ?? '도메인 미지정'}
        </p>
      </header>

      <div className="mb-4 border-b border-neutral-200">
        <nav className="-mb-px flex gap-4">
          <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')}>
            개요
          </TabBtn>
          <TabBtn active={tab === 'users'} onClick={() => setTab('users')}>
            사용자
          </TabBtn>
        </nav>
      </div>

      {tab === 'overview' ? (
        <div className="space-y-6">
          {customer.notes && (
            <div className="p-4 bg-surface border border-neutral-200 rounded-lg">
              <div className="text-xs text-neutral-500 uppercase mb-1">노트</div>
              <div className="text-sm whitespace-pre-wrap">{customer.notes}</div>
            </div>
          )}
          <div>
            <h2 className="text-sm font-medium text-neutral-700 mb-2">라이선스</h2>
            <div className="bg-surface border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">License Key</th>
                    <th className="text-left px-4 py-2 font-medium">등급</th>
                    <th className="text-left px-4 py-2 font-medium">종류</th>
                    <th className="text-left px-4 py-2 font-medium">만료</th>
                    <th className="text-left px-4 py-2 font-medium">활성화</th>
                    <th className="text-left px-4 py-2 font-medium">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {licenses.map((l) => {
                    const expired = l.expiresAt && new Date(l.expiresAt).getTime() < Date.now();
                    return (
                      <tr key={l.id}>
                        <td className="px-4 py-2 font-mono text-xs">{l.licenseKey}</td>
                        <td className="px-4 py-2 text-neutral-700 text-xs font-medium">
                          {levelLabelOf(l.level) ?? l.level ?? '사용자 지정'}
                        </td>
                        <td className="px-4 py-2 text-neutral-600">
                          {l.type === 'perpetual' ? '영구' : '구독'}
                        </td>
                        <td className="px-4 py-2 text-neutral-600 text-xs">
                          {l.expiresAt ? new Date(l.expiresAt).toLocaleDateString('ko-KR') : '—'}
                        </td>
                        <td className="px-4 py-2 text-neutral-600">
                          {l.activationCount} / {l.maxActivations}
                        </td>
                        <td className="px-4 py-2">
                          {l.revokedAt ? (
                            <span className="text-red-700 text-xs font-medium">revoked</span>
                          ) : expired ? (
                            <span className="text-amber-700 text-xs font-medium">expired</span>
                          ) : (
                            <span className="text-green-700 text-xs font-medium">active</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {licenses.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-neutral-500">
                        발급된 라이선스 없음
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <CustomerUsersTab fixedCustomerId={id} />
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pb-2 px-1 text-sm border-b-2 transition-colors ${
        active
          ? 'border-brand-500 text-brand-700 font-medium'
          : 'border-transparent text-neutral-600 hover:text-neutral-900'
      }`}
    >
      {children}
    </button>
  );
}
